<script setup>
import { ref, computed } from "vue"
import { Link } from "@inertiajs/vue3"
import { CHANNEL_CONFIG, DEFAULT_PLATFORMS, ALL_PLATFORMS } from "@/composables/useChannelConfig.js"
import Icon from "@/Components/UI/Icon.vue"

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  readonly:   { type: Boolean, default: false },
})
const emit = defineEmits(["update:modelValue"])

function buildRows(channels) {
  const saved = [...channels]
  const rows = DEFAULT_PLATFORMS.map(platform => {
    const existing = saved.find(c => c.platform === platform && !c._destroy)
    return existing
      ? { ...existing }
      : { platform, handle: "", custom_label: "", position: DEFAULT_PLATFORMS.indexOf(platform) }
  })
  saved.forEach(c => {
    if (!DEFAULT_PLATFORMS.includes(c.platform) && !c._destroy) {
      rows.push({ ...c })
    }
  })
  return rows
}

const rows = ref(buildRows(props.modelValue))

function emitUpdate() {
  const out = rows.value.map(r => {
    if (r.id && r.handle === "") return { ...r, _destroy: true }
    if (!r.id && r.handle === "") return null
    return { ...r }
  }).filter(Boolean)
  emit("update:modelValue", out)
}

function updateHandle(index, value) {
  rows.value[index].handle = value
  emitUpdate()
}

function updateCustomLabel(index, value) {
  rows.value[index].custom_label = value
  emitUpdate()
}

function removeRow(index) {
  const row = rows.value[index]
  if (DEFAULT_PLATFORMS.includes(row.platform)) {
    if (row.id) {
      rows.value[index] = { ...row, handle: "", custom_label: "", _destroy: true }
    } else {
      rows.value[index] = { ...row, handle: "", custom_label: "" }
    }
  } else {
    rows.value.splice(index, 1)
  }
  emitUpdate()
}

const pickerOpen = ref(false)
const pickerSearch = ref("")

const addedPlatforms = computed(() =>
  new Set(rows.value.filter(r => r.handle !== "" || DEFAULT_PLATFORMS.includes(r.platform)).map(r => r.platform))
)

const filteredPlatforms = computed(() => {
  const q = pickerSearch.value.toLowerCase()
  return ALL_PLATFORMS.filter(p => {
    if (p === "whatsapp" || p === "line" || p === "telegram") return false
    const label = CHANNEL_CONFIG[p].label.toLowerCase()
    return label.includes(q)
  })
})

function addPlatform(platform) {
  rows.value.push({
    platform,
    handle: "",
    custom_label: "",
    position: rows.value.length,
  })
  pickerOpen.value = false
  pickerSearch.value = ""
}

function closePicker() {
  pickerOpen.value = false
  pickerSearch.value = ""
}

function iconStyle(platform) {
  if (platform === "instagram") {
    return "background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)"
  }
  return `background: ${CHANNEL_CONFIG[platform]?.color ?? "#94a3b8"}`
}
</script>

<template>
  <div>
    <p class="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-3">Communication</p>

    <!-- Readonly mode -->
    <template v-if="readonly">
      <div v-if="modelValue.length === 0" class="text-sm text-[color:var(--muted)]">No channels recorded.</div>
      <ul v-else class="space-y-2">
        <li
          v-for="ch in modelValue"
          :key="ch.id ?? ch.platform"
          class="flex items-center gap-3"
        >
          <span
            class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
            :style="iconStyle(ch.platform)"
          >
            {{ CHANNEL_CONFIG[ch.platform]?.initials ?? "?" }}
          </span>
          <span class="text-sm text-[color:var(--inkSoft)] w-24 flex-shrink-0">
            {{ ch.platform === "other" ? ch.custom_label : CHANNEL_CONFIG[ch.platform]?.label }}
          </span>
          <a
            v-if="CHANNEL_CONFIG[ch.platform]?.deepLink(ch.handle)"
            :href="CHANNEL_CONFIG[ch.platform].deepLink(ch.handle)"
            target="_blank"
            rel="noopener"
            class="text-sm text-[color:var(--accent)] hover:underline truncate"
          >{{ ch.handle }}</a>
          <span v-else class="text-sm text-[color:var(--ink)]">{{ ch.handle }}</span>
          <Link
            v-if="ch.conversation_id"
            :href="`/inbox/conversations/${ch.conversation_id}`"
            class="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-full text-[color:var(--muted)] hover:text-[color:var(--accent)] hover:bg-[color:var(--surfaceAlt)] transition-colors"
            title="Open chat"
            aria-label="Open chat"
          >
            <Icon name="chat" :size="16" />
          </Link>
        </li>
      </ul>
    </template>

    <!-- Edit mode -->
    <template v-else>
      <div class="space-y-2">
        <div
          v-for="(row, index) in rows"
          :key="row.platform + index"
          v-show="!row._destroy"
          class="flex items-center gap-2"
        >
          <span
            class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
            :style="iconStyle(row.platform)"
          >
            {{ CHANNEL_CONFIG[row.platform]?.initials ?? "?" }}
          </span>

          <span class="text-sm text-[color:var(--inkSoft)] w-24 flex-shrink-0">
            <template v-if="row.platform === 'other'">
              <input
                :value="row.custom_label"
                type="text"
                placeholder="Label"
                class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-2 py-1 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
                @input="updateCustomLabel(index, $event.target.value)"
              />
            </template>
            <template v-else>
              {{ CHANNEL_CONFIG[row.platform]?.label }}
            </template>
          </span>

          <input
            :value="row.handle"
            type="text"
            :placeholder="row.platform === 'whatsapp' ? '+66 81 234 5678' : '@username'"
            class="flex-1 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-1.5 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            @input="updateHandle(index, $event.target.value)"
          />

          <button
            v-if="!DEFAULT_PLATFORMS.includes(row.platform) || row.handle"
            type="button"
            class="text-[color:var(--muted)] hover:text-[color:var(--danger)] transition-colors text-lg leading-none px-1"
            :aria-label="`Remove ${row.platform}`"
            @click="removeRow(index)"
          >×</button>
          <div v-else class="w-6"></div>
        </div>
      </div>

      <div class="relative mt-3">
        <button
          type="button"
          class="inline-flex items-center gap-2 px-3 py-1.5 rounded-[var(--radius-md)] border border-dashed text-sm font-medium transition-colors"
          :style="{ borderColor: 'var(--accent)', background: 'var(--accentSoft)', color: 'var(--accent)' }"
          @click="pickerOpen = !pickerOpen"
        >
          <span class="text-base leading-none">+</span> Add channel
        </button>

        <div
          v-if="pickerOpen"
          class="absolute left-0 top-full mt-1 z-20 w-56 rounded-[var(--radius-lg)] overflow-hidden"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.08))' }"
        >
          <div class="p-2 border-b border-[color:var(--line)]">
            <input
              v-model="pickerSearch"
              type="text"
              placeholder="Search platforms…"
              class="w-full px-2 py-1.5 text-sm rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
            />
          </div>

          <ul class="max-h-52 overflow-y-auto py-1">
            <li
              v-for="platform in filteredPlatforms"
              :key="platform"
            >
              <button
                type="button"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors"
                :class="addedPlatforms.has(platform)
                  ? 'opacity-40 cursor-not-allowed'
                  : 'hover:bg-[var(--surfaceAlt)] text-[color:var(--ink)]'"
                :disabled="addedPlatforms.has(platform)"
                @click="!addedPlatforms.has(platform) && addPlatform(platform)"
              >
                <span
                  class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  :style="iconStyle(platform)"
                >
                  {{ CHANNEL_CONFIG[platform].initials }}
                </span>
                {{ CHANNEL_CONFIG[platform].label }}
              </button>
            </li>
          </ul>

          <div class="p-2 border-t border-[color:var(--line)]">
            <button
              type="button"
              class="w-full text-xs text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
              @click="closePicker"
            >Close</button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
